import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

// 2026-09-15 local-first dashboard perf: bump this string whenever the SHAPE of any
// persisted query's response changes (new/renamed/removed field a component depends on).
// It's passed as `buster` below - persistQueryClient discards the ENTIRE old localStorage
// cache the instant this doesn't match, so a future deploy can never crash a returning
// user's app by handing old-shaped cached JSON to new code.
export const CACHE_SCHEMA_VERSION = "v1";

// 2026-09-16 REGRESSION FIX: measured actual payload sizes (curl) after the "WebView got
// slow after live-data" report. `dashboard-booster-pool`=105KB, `dashboard-wallet-summary`
// =80KB, `orders-my-full`=38KB, `dashboard-batch-summary`=25.7KB, `rebirth-summary`=26KB -
// these 5 alone total ~275KB of JSON that `createSyncStoragePersister` was re-stringifying
// and synchronously writing to localStorage on the JS main thread every time ANY of them
// updated - which the pre-existing "live data" mechanism (lib/refreshBus.js's 30s
// notification-poll broadcast + the after-every-mutation broadcast, both from 2026-09-14,
// unrelated to this persistence layer) triggers frequently. Android WebView's localStorage
// I/O is well known to be far slower than desktop Chrome's for writes this size - this is
// the concrete, measurable explanation for "same code, fast on Chrome, slow on WebView."
// FIX: removed the 5 large/high-churn payloads below from the persist allowlist entirely -
// they stay in react-query's normal IN-MEMORY cache (still instant-paint for any warm
// in-session navigation, which is the vast majority of real usage; only a true cold
// app-relaunch loses the pre-paint for these 5 specifically, showing a brief skeleton
// instead - a safe trade since financial data was always going to background-refetch on
// staleTime:0 anyway). This does NOT change the live-data/refresh mechanism itself, ONLY
// what gets written to disk. Kept allowlist is now capped at a few KB total.
// Explicit ALLOWLIST - default-deny. Only these exact query keys are ever written to
// localStorage. Every other query (any mutation result, auth tokens/OTPs/payment session
// ids - none of which are ever stored as react-query data anyway) is never persisted.
const PERSISTED_QUERY_KEYS = new Set([
  "member-me", // ~1.5KB
  "public-settings", // ~6.7KB, static
  "dashboard-rank", // ~0.3KB
  "dashboard-campaigns-eligible",
  "dashboard-rp-identities", // ~4.8KB
  "team-tree-root", // ~0.6KB
  "kb-categories", // static
]);

export const queryPersister = createSyncStoragePersister({
  storage: window.localStorage,
  key: "vjgv-query-cache",
  throttleTime: 3000, // batches bursts of updates (e.g. one "member" broadcast touching
  // several allow-listed queries at once) into a single write instead of one per query.
});

export const persistOptions = {
  persister: queryPersister,
  buster: CACHE_SCHEMA_VERSION,
  maxAge: 24 * 60 * 60 * 1000, // anything older than 24h is dropped, full cold fetch instead
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => PERSISTED_QUERY_KEYS.has(query.queryKey[0]),
  },
};
